if (typeof debounce === 'undefined') {
  /**
   * Debounce function to limit the rate at which a function gets called.
   * @param {Function} fn - The function to debounce.
   * @param {number} wait - The delay in milliseconds.
   * @returns {Function} - The debounced function.
   */
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

/**
 *  @class ThemeHeader
 *  @function Manages all header behaviors including a unified control system for all dropdown menus.
 *  Version: 3.0 (Unified Menu Controller)
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.headerOffset = null; // Store the initial offset
    }

    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      // Calculate and store the initial header offset once
      if (this.classList.contains('header-sticky--active')) {
        this.headerOffset = this.getBoundingClientRect().top + window.scrollY;
      }

      // PC Desktop menu control - exclude mobile menu
      const desktopMenuContainer = this.querySelector('.thb-full-menu');
      if (desktopMenuContainer) {
        const allParentMenuItems = desktopMenuContainer.querySelectorAll('.menu-item-has-children');
        let menuHideTimer = null;

        // Add header protection to prevent menu from closing when hovering over header
        const headerElement = this;
        headerElement.addEventListener('mouseenter', () => {
          if (menuHideTimer) {
            clearTimeout(menuHideTimer);
            menuHideTimer = null;
          }
        });

        // Global cleanup function to ensure overlay is removed properly
        const cleanupMegaMenuOverlay = () => {
          const hasActiveMegaMenu = Array.from(allParentMenuItems).some(item =>
            item.classList.contains('menu-item--active') && item.classList.contains('menu-item-has-megamenu')
          );

          if (!hasActiveMegaMenu) {
            document.body.classList.remove('body-megamenu-open');
          }
        };

        // Add global mouse tracking to clean up overlay when needed
        document.addEventListener('mousemove', debounce(() => {
          cleanupMegaMenuOverlay();
        }, 100));

        // Clean up overlay when page loses focus or user scrolls
        document.addEventListener('blur', cleanupMegaMenuOverlay);
        window.addEventListener('scroll', debounce(cleanupMegaMenuOverlay, 200));

      allParentMenuItems.forEach((item, index) => {
        const menuTitle = item.querySelector('a').textContent.trim();

        item.addEventListener('mouseenter', (event) => {
          const currentItem = event.currentTarget;

          if (menuHideTimer) {
            clearTimeout(menuHideTimer);
            menuHideTimer = null;
          }

          allParentMenuItems.forEach(otherItem => {
            if (otherItem !== currentItem) {
              otherItem.classList.remove('menu-item--active');
              otherItem.classList.remove('menu-item--hiding');
            }
          });

          currentItem.classList.remove('menu-item--hiding');
          currentItem.classList.add('menu-item--active');

          if (currentItem.classList.contains('menu-item-has-megamenu')) {
            document.body.classList.add('body-megamenu-open');
          }
        });

        item.addEventListener('mouseleave', (event) => {
          const currentItem = event.currentTarget;

          menuHideTimer = setTimeout(() => {
            currentItem.classList.remove('menu-item--active');
            currentItem.classList.remove('menu-item--hiding');

            if (currentItem.classList.contains('menu-item-has-megamenu')) {
              document.body.classList.remove('body-megamenu-open');
            }

            menuHideTimer = null;
          }, 50);
        });

        const subMenu = item.querySelector('.sub-menu');
        if (subMenu) {
          subMenu.addEventListener('mouseenter', () => {
            if (menuHideTimer) {
              clearTimeout(menuHideTimer);
              menuHideTimer = null;
            }
            item.classList.remove('menu-item--hiding');
            item.classList.add('menu-item--active');
          });

          subMenu.addEventListener('mouseleave', () => {
            menuHideTimer = setTimeout(() => {
              item.classList.remove('menu-item--active');
              item.classList.remove('menu-item--hiding');

              if (item.classList.contains('menu-item-has-megamenu')) {
                document.body.classList.remove('body-megamenu-open');
              }

              menuHideTimer = null;
            }, 50);
          });
        }

        if (item.classList.contains('menu-item-has-megamenu')) {
          // Find the mega menu container - prioritize the full container over inner elements
          const megaMenuContainer = item.querySelector('.sub-menu.mega-menu-container') ||
                                   item.querySelector('.mega-menu-container--inner-has-sidebar') ||
                                   item.querySelector('.mega-menu-sidebar, .mega-menu-columns');

          if (megaMenuContainer) {
            megaMenuContainer.addEventListener('mouseenter', () => {
              if (menuHideTimer) {
                clearTimeout(menuHideTimer);
                menuHideTimer = null;
              }
              item.classList.remove('menu-item--hiding');
              item.classList.add('menu-item--active');
            });

            megaMenuContainer.addEventListener('mouseleave', () => {
              menuHideTimer = setTimeout(() => {
                item.classList.remove('menu-item--active');
                item.classList.remove('menu-item--hiding');

                if (item.classList.contains('menu-item-has-megamenu')) {
                  document.body.classList.remove('body-megamenu-open');
                }

                menuHideTimer = null;
              }, 50);
            });
          }
        }
      });
      } // End of desktop menu control

      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          if (this.toggle.classList.contains('active')) {
            this.toggle.querySelector('.mobile-toggle').click();
          }
        }
      });

      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }

      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        const isActive = this.toggle.classList.contains('active');

        if (isActive) {
          // Closing menu
          document.body.classList.remove('overflow-hidden');
          document.body.classList.remove('mobile-menu-open');
          document.body.style.overflow = '';
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          // Opening menu
          document.body.classList.add('overflow-hidden');
          document.body.classList.add('mobile-menu-open');
          document.body.style.overflow = 'hidden';
          // Use setTimeout to allow details to open first
          setTimeout(() => {
            this.toggle.classList.add('active');
          }, 10);
        }
      });
      
      window.addEventListener('scroll', this.setStickyClass.bind(this), { passive: true });
      window.addEventListener('scroll', debounce(this.setHeaderOffset.bind(this), 50), { passive: true });
      window.addEventListener('scroll', debounce(this.setHeaderHeight.bind(this), 50), { passive: true });

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('resize', debounce(this.setAnnouncementHeight.bind(this), 100));
        // Delay initial calculation to ensure content is rendered
        setTimeout(() => {
          this.setAnnouncementHeight();
        }, 0);
      }
      
      window.dispatchEvent(new Event('scroll'));

      setTimeout(() => {
        this.mobile_nav = document.querySelector('.header-mobile-navigation');
        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);

      if (this.menu) {
        this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
        this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
      }
    }

    setStickyClass() {
      if (this.classList.contains('header-sticky--active') && this.headerOffset !== null) {
        const shouldBeSticky = window.scrollY >= this.headerOffset && window.scrollY > 0;
        const isMobile = window.innerWidth <= 1024;
        const isCurrentlySticky = this.classList.contains('is-sticky');

        // Only update if state actually changed
        if (shouldBeSticky !== isCurrentlySticky) {
          this.classList.toggle('is-sticky', shouldBeSticky);

          // On mobile transparent headers, sync mobile-sticky with is-sticky
          if (isMobile && this.classList.contains('transparent--true')) {
            if (shouldBeSticky) {
              this.classList.add('mobile-sticky');
            } else {
              this.classList.remove('mobile-sticky');
            }
          }
        }
      }
    }
    setAnnouncementHeight() { const a_bar = document.querySelector('.announcement-bar-section'); if (a_bar) { const h = a_bar.clientHeight; document.documentElement.style.setProperty('--announcement-height', `${h}px`); } }
    setHeaderOffset() { const h = this.header_section.getBoundingClientRect().top; document.documentElement.style.setProperty('--header-offset', `${h}px`); }
    setHeaderHeight() { const h = this.clientHeight; document.documentElement.style.setProperty('--header-height', `${h}px`); }
    onSummaryClick(event) { const detailsElement = event.currentTarget.parentNode; if (!detailsElement.hasAttribute('open')) { detailsElement.classList.add('menu-opening'); detailsElement.closest('.link-container')?.classList.add('submenu-open'); } }
    onCloseButtonClick(event) { event.preventDefault(); this.closeSubmenu(event.currentTarget.closest('details')); }
    closeSubmenu(detailsElement) { detailsElement.classList.remove('menu-opening'); detailsElement.closest('.submenu-open')?.classList.remove('submenu-open'); this.closeAnimation(detailsElement); }
    closeAnimation(detailsElement) { setTimeout(() => { detailsElement.removeAttribute('open'); }, 300); }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class FullMenu
 *  @function Manages standard dropdown menu positioning to avoid going off-screen.
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
      this.submenus = this.querySelectorAll('.thb-full-menu > .menu-item-has-children:not(.menu-item-has-megamenu) > .sub-menu');
    }

    connectedCallback() {
      if (!this.submenus.length) return;
      const resizeMenus = this.resizeSubMenus.bind(this);
      document.fonts.ready.then(() => {
        resizeMenus();
        window.addEventListener('resize', debounce(resizeMenus, 150));
      });
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        const sub_submenus = submenu.querySelectorAll(':scope > .menu-item-has-children > .sub-menu');
        sub_submenus.forEach((sub_submenu) => {
          const parentRect = sub_submenu.parentElement.getBoundingClientRect();
          const isOffScreen = (parentRect.left + parentRect.width + sub_submenu.offsetWidth) > window.innerWidth;
          sub_submenu.parentElement.classList.toggle('left-submenu', isOffScreen);
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}